import type {
  ExternalUrl,
  PlaybookAsset,
  PlaybookAssetType,
} from '@carboncopyco/types';
import {
  InlineLink,
  TextBase,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { Link } from 'react-router-dom';
import { TableInfoIcon } from '../../../Dashboard/TableInfoIcon';
import { iconByAssetType } from '../../../PlaybookModal/consts';
import { usePlaybookPageProps } from '../PlaybookPageContext';
import { getIconTitle, getPlaybookAssetTitle } from '../utils';

export type AssetNameLink = {
  asset: PlaybookAsset;
  index: number;
};

type DeletedAssetTitleProps = {
  label: string;
  assetType: PlaybookAssetType;
};

const DeletedAssetTitle = ({ label, assetType }: DeletedAssetTitleProps) => {
  return (
    <div className="flex items-center gap-1.5">
      <div className="truncate">
        <TextBase
          label={label}
          color="text-ds-natural-600"
          ellipsis
        />
      </div>
      <TableInfoIcon
        label={`${capitalize(assetType)} is archived and cannot be accessed`}
      />
    </div>
  );
};

export const AssetNameLink = ({ asset, index }: AssetNameLink) => {
  const { demoPageLink, tourPageLink, readOnly } = usePlaybookPageProps();

  const { assetType, assetId, entity } = asset;

  return (
    <div className="flex items-center gap-1 max-w-[320px]">
      <div className="w-4 shrink-0">
        <TextCaption label={index + 1} />
      </div>
      <div className="flex gap-2.5 items-center">
        <TooltipBubble
          title={getIconTitle(entity)}
          placement="top"
        >
          {iconByAssetType[assetType]}
        </TooltipBubble>
        <TooltipBubble
          title={getPlaybookAssetTitle(entity)}
          placement="top"
        >
          <div className="truncate max-w-[320px]">
            {assetType === 'externalUrl' ? (
              <TooltipBubble
                title={(entity as ExternalUrl).url}
                placement="top"
              >
                <InlineLink
                  disabled={readOnly}
                  label={getPlaybookAssetTitle(entity)}
                  color="text-ds-natural-600"
                  href={(entity as ExternalUrl).url}
                  target="_blank"
                  ellipsis
                />
              </TooltipBubble>
            ) : entity.isDeleted ? (
              <DeletedAssetTitle
                label={getPlaybookAssetTitle(entity)}
                assetType={assetType}
              />
            ) : (
              <InlineLink
                disabled={readOnly}
                as={Link}
                to={
                  assetType === 'tour'
                    ? tourPageLink(Number(assetId))
                    : demoPageLink(Number(assetId))
                }
                label={getPlaybookAssetTitle(asset.entity)}
                color="text-ds-natural-600"
                ellipsis
              />
            )}
          </div>
        </TooltipBubble>
      </div>
    </div>
  );
};
